import React, { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	Box,
	Flex,
	Text,
	Tab,
	Tabs,
	TabPanel,
	TabPanels,
	TabList,
	Button,
	ModalBody,
	ModalFooter,
	Table,
	Td,
	Thead,
	Tr,
} from "@chakra-ui/react";
import { Tbody } from "@familyzone/component-library";
import { useToast } from "@/components/ui/Toast";
import { SplitModal } from "./SplitModal";
import { ClassroomOverviewDTO } from "@/stores/classroomStore_I";
import { useClassroomDetailsQuery } from "@/query/classroomDetailsQuery";
import useStore from "@/stores/index";
import { getClassroomStore } from "@/stores/classroomStore";
import { ClassCombinationResponse, GetClassCombinations } from "@/js/api/combineClassesApi";
import { getProviderDisplayText2 } from "@/js/utils/providerUtils";
import { colours } from "@/utils/themeUtils";
import { useUserSessionStore } from "@/stores/v2/userSessionStore";
import { Notice } from "@/components/Notice";
import { ClasswizeModal } from "@/components/ui/Modal";
import { TableContainer, Th } from "@/components/ui/Table";
import { ModifyTeachersContent } from "@/components/ModifyTeachersContent";

type props = {
	open: boolean;
	onClose: () => void;
	classroom: ClassroomOverviewDTO;
};

export const SettingsModal: FC<props> = ({ open, onClose, classroom }) => {
	const { t } = useTranslation();
	const { successToast, errorToast } = useToast();
	const tabs = useMemo(() => [t("overview.settingsClassesTab"), t("overview.settingsTeachersTab")], [t]);
	const columns = useMemo(
		() => [
			{ headerText: t("overview.className"), columnName: "className" },
			{ headerText: t("overview.provider"), columnName: "provider" },
		],
		[t]
	);
	const [tabIndex, setTabIndex] = useState(0);
	const [splitModalOpen, setSplitModalOpen] = useState(false);
	const [classCombination, setClassCombination] = useState<ClassCombinationResponse[]>([]);
	const [isClassCombinationsLoaded, setIsClassCombinationsLoaded] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const teacherId = useUserSessionStore.getState().getTeacherId() as string;
	const classroomDetailsQuery = useClassroomDetailsQuery(classroom.id, true, true, false, classroom.type, classroom.sourceId, teacherId, {
		enabled: open,
	});

	const classNotAvailable = useMemo(
		() => isClassCombinationsLoaded && classCombination.length === 0,
		[isClassCombinationsLoaded, classCombination]
	);

	const handleDelete = async () => {
		const { handleDeleteClassroom, fetchClassrooms } = getClassroomStore(useStore.getState());

		try {
			setIsSaving(true);

			await handleDeleteClassroom(classroom.stableIdV1);
			await fetchClassrooms();

			successToast({
				title: `${title} ${t("overview.settingsDeleteSuccessSuffix")}`,
				isClosable: true,
			});
		} catch (error) {
			console.error("Error deleting classroom:", error);
			errorToast({
				title: `${t("overview.settingsDeleteErrorPrefix")} ${title}`,
				description: t("overview.settingsDeleteErrorDescription"),
				isClosable: true,
			});
		} finally {
			setIsSaving(false);
			onClose();
		}
	};

	const onCloseSplitModal = () => {
		setSplitModalOpen(false);
		if (isSaving) onClose();
	};

	const tableClassDataMap = (item: ClassCombinationResponse, index: number) => (
		<Tr key={index}>
			<Td>
				<Text fontSize="md">{item.displayName || item.sourceName}</Text>
			</Td>
			<Td>
				<Text fontSize="md">{getProviderDisplayText2(item.sourceType, item.classType)}</Text>
			</Td>
		</Tr>
	);

	const title = useMemo(() => classroom.personalisation?.displayName ?? "", [classroom]);

	useEffect(() => {
		if (!open) {
			return;
		}

		void (async () => {
			setIsClassCombinationsLoaded(false);

			const { getTeacherStableIdV1 } = getClassroomStore(useStore.getState());
			const classCombinations = await GetClassCombinations(classroom.stableIdV1, getTeacherStableIdV1());
			setClassCombination(classCombinations);

			setIsClassCombinationsLoaded(true);
		})();
	}, [classroom, open]);

	const renderButtons = () => {
		if (tabIndex === 0) {
			if (classNotAvailable) {
				return (
					<Button
						variant="danger"
						onClick={handleDelete}
						data-testid="modal-next-btn"
						isDisabled={!isClassCombinationsLoaded}
						isLoading={isSaving}
					>
						{t("overview.settingsDeleteClass")}
					</Button>
				);
			}

			return (
				<Button
					onClick={() => setSplitModalOpen(true)}
					data-testid="modal-next-btn"
					isDisabled={!isClassCombinationsLoaded}
					isLoading={isSaving}
				>
					{t("overview.settingsSplitClasses")}
				</Button>
			);
		}

		return <></>;
	};

	return (
		<>
			<ClasswizeModal isOpen={open} onClose={onClose} title={title} size="2xl">
				<ModalBody data-testid="settings-modal-body">
					<Tabs onChange={(index) => setTabIndex(index)}>
						<TabList>
							{tabs.map((tab) => (
								<Tab key={tab}>{tab}</Tab>
							))}
						</TabList>

						<TabPanels>
							<TabPanel>
								<Flex direction="column">
									<Flex>
										<Box color={colours.navy[800]} fontFamily="Roboto" fontSize="14px" fontWeight={400} lineHeight="20px">
											{t("overview.settingsDescriptiveTextClasses")}
										</Box>
									</Flex>
									<Flex mt="16px" direction="row">
										<TableContainer>
											<Table variant="qoriaStriped">
												<Thead>
													<Tr>
														{columns.map(({ headerText, columnName }) => (
															<Th key={columnName} fontSize={"sm"}>
																{headerText}
															</Th>
														))}
													</Tr>
												</Thead>
												<Tbody loaded={isClassCombinationsLoaded}>{classCombination.map(tableClassDataMap)}</Tbody>
											</Table>
										</TableContainer>
									</Flex>
								</Flex>
							</TabPanel>
							<TabPanel>
								<ModifyTeachersContent
									classroom={classroom}
									classroomDetails={classroomDetailsQuery.data?.classroom}
									isTeachersLoaded={classroomDetailsQuery.isSuccess}
								/>
							</TabPanel>
						</TabPanels>
					</Tabs>
					{classNotAvailable && <Notice status="info" children={t("overview.settingsNotAvailableNotice")} />}
				</ModalBody>

				<ModalFooter>
					<Flex direction="row-reverse">{renderButtons()}</Flex>
				</ModalFooter>
			</ClasswizeModal>
			<SplitModal
				open={splitModalOpen}
				onClose={onCloseSplitModal}
				classroom={classroom}
				classCombination={classCombination}
				isSaving={isSaving}
				setIsSaving={setIsSaving}
			/>
		</>
	);
};
